import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';
import { 
  MessageSquare, Plus, Send, Loader2, RefreshCw, Clock, CheckCircle,
  HelpCircle, AlertCircle, Lightbulb, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIAS = [
  { id: 'Duvida', label: 'Dúvida', icon: HelpCircle },
  { id: 'Pedido_Info', label: 'Pedido de Informação', icon: AlertCircle },
  { id: 'Problema', label: 'Problema Técnico', icon: AlertCircle },
  { id: 'Sugestao', label: 'Sugestão', icon: Lightbulb }
];

const STATUS_CONFIG = {
  'Aberto': { color: 'bg-amber-500', label: 'Aberto' },
  'Respondido': { color: 'bg-blue-500', label: 'Respondido' },
  'Fechado': { color: 'bg-green-500', label: 'Fechado' }
};

const FAQ_ITEMS = [
  {
    pergunta: 'Como atualizar os dados da minha instituição?',
    resposta: 'Aceda ao menu "Minha Instituição" na barra lateral. Pode editar todos os dados básicos, localização, contactos e caracterização. Algumas alterações significativas podem requerer aprovação da ENTRAJUDA.'
  },
  {
    pergunta: 'Como submeter um pedido de recursos?',
    resposta: 'Aceda ao menu "Pedidos de Apoio" e clique em "Novo Pedido". Siga os 3 passos: selecione o tipo de recurso, preencha os detalhes e adicione uma justificação. O pedido será analisado pela equipa EA.'
  },
  {
    pergunta: 'Quanto tempo demora a resposta a um pedido?',
    resposta: 'O prazo médio de resposta é de 5-10 dias úteis. Pedidos urgentes são priorizados. Pode acompanhar o estado do seu pedido na página de Pedidos.'
  },
  {
    pergunta: 'Como adicionar utilizadores à minha instituição?',
    resposta: 'Aceda a "Definições" > "Utilizadores". Apenas administradores podem adicionar novos utilizadores. O novo utilizador receberá um email com as credenciais de acesso.'
  },
  {
    pergunta: 'Como renovar documentos expirados?',
    resposta: 'Aceda à página "Documentos" e carregue uma nova versão do documento. O sistema irá notificá-lo automaticamente quando os documentos estiverem próximos da data de validade.'
  }
];

export default function PortalMensagensPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mensagens, setMensagens] = useState([]);
  const [novaModalOpen, setNovaModalOpen] = useState(false);
  const [detalheModalOpen, setDetalheModalOpen] = useState(false);
  const [selectedMensagem, setSelectedMensagem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [novaForm, setNovaForm] = useState({
    assunto: '',
    categoria: '',
    mensagem: ''
  });
  
  const [respostaText, setRespostaText] = useState('');

  useEffect(() => {
    loadMensagens();
  }, []);

  const loadMensagens = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/mensagens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMensagens(data);
      } else if (response.status === 401) {
        navigate('/portal/login');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCriarMensagem = async () => {
    if (!novaForm.assunto || !novaForm.categoria || !novaForm.mensagem) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/mensagens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(novaForm)
      });
      
      if (response.ok) {
        toast.success('Mensagem enviada!');
        setNovaModalOpen(false);
        setNovaForm({ assunto: '', categoria: '', mensagem: '' });
        loadMensagens();
      } else {
        toast.error('Erro ao enviar');
      }
    } catch (error) {
      toast.error('Erro ao enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResponder = async () => {
    if (!respostaText.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/mensagens/${selectedMensagem.id}/responder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mensagem: respostaText })
      });
      
      if (response.ok) {
        toast.success('Resposta enviada!');
        setRespostaText('');
        loadMensagens();
        // Refresh selected
        const updated = await response.json();
        setSelectedMensagem(prev => ({ ...prev, mensagens: [...(prev.mensagens || []), updated.nova_mensagem] }));
      }
    } catch (error) {
      toast.error('Erro ao responder');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (msg) => {
    setSelectedMensagem(msg);
    setDetalheModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-mensagens-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-blue-600" />
            Mensagens e Suporte
          </h1>
          <p className="text-slate-500">Contacte a equipa ENTRAJUDA</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadMensagens}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setNovaModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Mensagem
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Conversas ({mensagens.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {mensagens.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 mb-4">Ainda não tem mensagens</p>
                  <Button onClick={() => setNovaModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Enviar primeira mensagem
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {mensagens.map((msg) => (
                    <div
                      key={msg.id}
                      onClick={() => openDetail(msg)}
                      className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${STATUS_CONFIG[msg.status]?.color || 'bg-slate-500'} text-white text-xs`}>
                              {STATUS_CONFIG[msg.status]?.label || msg.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {CATEGORIAS.find(c => c.id === msg.categoria)?.label || msg.categoria}
                            </Badge>
                          </div>
                          <p className="font-medium truncate">{msg.assunto}</p>
                          <p className="text-sm text-slate-500 truncate mt-1">
                            {msg.mensagens?.[msg.mensagens.length - 1]?.texto?.substring(0, 80) || 'Sem mensagens'}...
                          </p>
                        </div>
                        <div className="text-right text-xs text-slate-400 ml-4">
                          <p>{new Date(msg.updated_at || msg.created_at).toLocaleDateString('pt-PT')}</p>
                          <ChevronRight className="w-4 h-4 ml-auto mt-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                Perguntas Frequentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {FAQ_ITEMS.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-sm text-left">
                      {faq.pergunta}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600">
                      {faq.resposta}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">Não encontrou resposta?</p>
                <Button size="sm" onClick={() => setNovaModalOpen(true)} className="w-full">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Contacte-nos
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="pt-6">
              <h4 className="font-medium mb-2">Contactos Diretos</h4>
              <div className="space-y-2 text-sm">
                <p>📧 suporte@entrajuda.pt</p>
                <p>📞 213 600 500</p>
                <p className="text-slate-500">Seg-Sex: 9h-18h</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Nova Mensagem Modal */}
      <Dialog open={novaModalOpen} onOpenChange={setNovaModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Mensagem</DialogTitle>
            <DialogDescription>
              Envie uma mensagem para a equipa ENTRAJUDA
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Assunto *</Label>
              <Input
                value={novaForm.assunto}
                onChange={(e) => setNovaForm(prev => ({ ...prev, assunto: e.target.value }))}
                placeholder="Resumo da sua questão"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={novaForm.categoria} onValueChange={(v) => setNovaForm(prev => ({ ...prev, categoria: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Mensagem *</Label>
              <Textarea
                value={novaForm.mensagem}
                onChange={(e) => setNovaForm(prev => ({ ...prev, mensagem: e.target.value }))}
                placeholder="Descreva a sua questão em detalhe..."
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCriarMensagem} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalhe Modal */}
      <Dialog open={detalheModalOpen} onOpenChange={setDetalheModalOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedMensagem?.assunto}</DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={`${STATUS_CONFIG[selectedMensagem?.status]?.color} text-white`}>
                {STATUS_CONFIG[selectedMensagem?.status]?.label}
              </Badge>
              <Badge variant="outline">
                {CATEGORIAS.find(c => c.id === selectedMensagem?.categoria)?.label}
              </Badge>
            </div>
          </DialogHeader>
          
          {selectedMensagem && (
            <div className="space-y-4">
              {/* Message Thread */}
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {(selectedMensagem.mensagens || []).map((msg, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg ${
                      msg.origem === 'instituicao' 
                        ? 'bg-blue-50 ml-4' 
                        : 'bg-slate-100 mr-4'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>{msg.origem === 'instituicao' ? 'Você' : 'ENTRAJUDA'}</span>
                      <span>{new Date(msg.data).toLocaleString('pt-PT')}</span>
                    </div>
                    <p className="text-sm">{msg.texto}</p>
                  </div>
                ))}
              </div>
              
              {/* Reply if not closed */}
              {selectedMensagem.status !== 'Fechado' && (
                <div className="border-t pt-4">
                  <Label className="mb-2 block">Responder</Label>
                  <Textarea
                    value={respostaText}
                    onChange={(e) => setRespostaText(e.target.value)}
                    placeholder="Escreva a sua resposta..."
                    rows={3}
                  />
                  <Button onClick={handleResponder} disabled={submitting || !respostaText.trim()} className="mt-2">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Enviar
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalheModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
